// ImpersonationBanner — fixed orange strip across the top of every
// authenticated page while an impersonation session is active.
//
// Design (from the spec):
//   * Orange #F97316 — not red. Red implies error; impersonation is
//     normal system_admin behavior.
//   * Fixed at top, 48px tall, not dismissable.
//   * Left: "VIEWING AS [Name] · [Role]".
//   * Right: "Return to my view" button.
//   * Page content shifts down by 48px (styled in styles.css via
//     `body.is-impersonating` — added/removed by the banner itself).
//
// Kept deliberately lean: it consumes the session context's
// `stopImpersonation` action and navigates to /dashboard after it
// completes, so the user lands on their OWN cockpit (not the target
// user's dashboard URL). If the stop fails, the session refetch in
// stopImpersonation still runs, so the UI recovers naturally.

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSession } from "../session";
import {
  formatImpersonationTargetLabel,
  isAdminOnAdminTarget,
  takeImpersonationReturnPath,
} from "./impersonation-banner-helpers";

export function ImpersonationBanner() {
  const { isImpersonating, user, stopImpersonation } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  // Add/remove the body class so page content can shift down exactly
  // when the banner is present. Using a body class rather than a
  // prop-threaded layout offset keeps every page layout simple.
  // Admin-on-admin impersonation also toggles a second class so the
  // banner can render its deeper-orange variant AND (future) any
  // other page chrome that wants to react to the sensitivity level.
  const adminOnAdmin = user ? isAdminOnAdminTarget(user) : false;
  useEffect(() => {
    if (isImpersonating) {
      document.body.classList.add("is-impersonating");
      if (adminOnAdmin) {
        document.body.classList.add("is-impersonating-admin-on-admin");
      }
      return () => {
        document.body.classList.remove("is-impersonating");
        document.body.classList.remove("is-impersonating-admin-on-admin");
      };
    }
    return undefined;
  }, [isImpersonating, adminOnAdmin]);

  const handleReturn = useCallback(async () => {
    setBusy(true);
    try {
      await stopImpersonation();
    } finally {
      setBusy(false);
      // Return to where the admin started impersonating (e.g. the Team list),
      // falling back to their own dashboard (2026-07-23 audit P6-4). The Team
      // page restores the saved scroll offset on mount. The target user's own
      // URL is meaningless once we revert, so we never keep that.
      const returnPath = takeImpersonationReturnPath();
      await navigate(returnPath ?? "/dashboard");
    }
  }, [stopImpersonation, navigate]);

  if (!isImpersonating || !user) {
    return null;
  }

  return (
    <div
      className={`impersonation-banner${adminOnAdmin ? " is-admin-on-admin" : ""}`}
      role="status"
      aria-live="polite"
      data-testid="impersonation-banner"
      data-admin-on-admin={adminOnAdmin ? "true" : "false"}
    >
      <div className="impersonation-banner-content">
        <span className="impersonation-banner-label">
          VIEWING AS {formatImpersonationTargetLabel(user)}
        </span>
        <button
          type="button"
          className="impersonation-banner-return"
          onClick={handleReturn}
          disabled={busy}
          data-testid="impersonation-return-btn"
        >
          {busy ? "Returning…" : "Return to my view"}
        </button>
      </div>
    </div>
  );
}
